import React, { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { loadFavicon } from "@/lib/favicon";

import { ThemeProvider } from "@/context/ThemeContext";
import { I18nProvider } from "@/lib/i18n";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import PublicLayout from "@/layouts/PublicLayout";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import CategoryPage from "@/pages/CategoryPage";
import Packages from "@/pages/Packages";
import Portfolio from "@/pages/Portfolio";
import HowItWorks from "@/pages/HowItWorks";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import Quote from "@/pages/Quote";
import PackageCheckout from "@/pages/PackageCheckout";
import Shop from "@/pages/shop/Shop";
import ProductDetail from "@/pages/shop/ProductDetail";
import Cart from "@/pages/shop/Cart";
import LegalPage from "@/pages/LegalPage";
import Withdrawals from "@/pages/portal/Withdrawals";
import AdminWithdrawals from "@/pages/admin/Withdrawals";
import NotFound from "@/pages/NotFound";
import PaymentReturn from "@/pages/PaymentReturn";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import GoogleCallback from "@/pages/auth/GoogleCallback";
import Forgot from "@/pages/auth/Forgot";
import Reset from "@/pages/auth/Reset";

import PortalLayout from "@/pages/portal/PortalLayout";
import PortalDashboard from "@/pages/portal/Dashboard";
import PortalList from "@/pages/portal/PortalList";
import PortalProjectDetail from "@/pages/portal/ProjectDetail";
import PortalMessages from "@/pages/portal/Messages";
import PortalTickets from "@/pages/portal/Tickets";
import PortalProfile from "@/pages/portal/Profile";
import PortalPurchases from "@/pages/portal/Purchases";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminResource from "@/pages/admin/AdminResource";
import AdminQuotes from "@/pages/admin/Quotes";
import AdminOffers from "@/pages/admin/Offers";
import AdminInvoices from "@/pages/admin/Invoices";
import AdminProjects from "@/pages/admin/Projects";
import AdminCustomers from "@/pages/admin/Customers";
import AdminTickets from "@/pages/admin/Tickets";
import AdminSettings from "@/pages/admin/Settings";
import AdminEmailConfig from "@/pages/admin/EmailConfig";
import AdminEmailTemplates from "@/pages/admin/EmailTemplates";
import AdminPaymentConfig from "@/pages/admin/PaymentConfig";
import AdminShop from "@/pages/admin/Shop";
import AdminSimple from "@/pages/admin/Simple";

function App() {
  useEffect(() => { loadFavicon(); }, []);
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Toaster position="top-right" richColors />
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/web-development" element={<CategoryPage category="web" />} />
                <Route path="/mobile-development" element={<CategoryPage category="mobile" />} />
                <Route path="/business-software" element={<CategoryPage category="software" />} />
                <Route path="/packages" element={<Packages />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/cart" element={<Cart />} />
                <Route path="/shop/:slug" element={<ProductDetail />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/quote" element={<Quote />} />
                <Route path="/checkout/package/:id" element={<ProtectedRoute><PackageCheckout /></ProtectedRoute>} />
                <Route path="/withdraw" element={<Withdrawals publicMode />} />
                <Route path="/legal/:type" element={<LegalPage />} />
                <Route path="/payment-return" element={<PaymentReturn />} />
                <Route path="*" element={<NotFound />} />
              </Route>

              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/callback" element={<GoogleCallback />} />
              <Route path="/forgot-password" element={<Forgot />} />
              <Route path="/reset-password" element={<Reset />} />

              <Route path="/portal" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
                <Route index element={<PortalDashboard />} />
                <Route path="offers" element={<PortalList type="offers" />} />
                <Route path="orders" element={<PortalList type="orders" />} />
                <Route path="projects" element={<PortalList type="projects" />} />
                <Route path="projects/:id" element={<PortalProjectDetail />} />
                <Route path="invoices" element={<PortalList type="invoices" />} />
                <Route path="files" element={<PortalList type="files" />} />
                <Route path="messages" element={<PortalMessages />} />
                <Route path="tickets" element={<PortalTickets />} />
                <Route path="purchases" element={<PortalPurchases />} />
                <Route path="withdrawals" element={<Withdrawals />} />
                <Route path="profile" element={<PortalProfile />} />
              </Route>

              <Route path="/admin" element={<ProtectedRoute staff><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="services" element={<AdminResource resource="services" />} />
                <Route path="packages" element={<AdminResource resource="packages" />} />
                <Route path="portfolio" element={<AdminResource resource="portfolio" />} />
                <Route path="blog" element={<AdminResource resource="blog" />} />
                <Route path="testimonials" element={<AdminResource resource="testimonials" />} />
                <Route path="faqs" element={<AdminResource resource="faqs" />} />
                <Route path="quotes" element={<AdminQuotes />} />
                <Route path="offers" element={<AdminOffers />} />
                <Route path="orders" element={<AdminSimple type="orders" />} />
                <Route path="invoices" element={<AdminInvoices />} />
                <Route path="projects" element={<AdminProjects />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="tickets" element={<AdminTickets />} />
                <Route path="messages" element={<PortalMessages admin />} />
                <Route path="leads" element={<AdminSimple type="leads" />} />
                <Route path="payments" element={<AdminSimple type="payments" />} />
                <Route path="shop" element={<AdminShop />} />
                <Route path="payment-methods" element={<AdminPaymentConfig />} />
                <Route path="email-config" element={<AdminEmailConfig />} />
                <Route path="email-templates" element={<AdminEmailTemplates />} />
                <Route path="withdrawals" element={<AdminWithdrawals />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Routes>
          </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;


